import { useEffect, useRef, useState } from "react";
import {
  ModalOverlay,
  ModalTitle,
  ModalField,
  StyledInput,
  StyledTextarea,
  ModalContentWrapper,
  ScrollableContent,
  FixedFooter,
  AddButton,
} from "./EditSectionModalStyles";
import ButtonGradient from "../ui/ButtonGradient";
import {
  EditSectionModalProps,
  MultiLinkItem as MultiItem,
} from "../../types/componentsConstructors";

const EditSectionModal = ({
  title,
  type,
  value,
  onClose,
  onSave,
  onDismissEmpty,
}: EditSectionModalProps) => {
  const initialValue =
    type === "multiLink"
      ? ((Array.isArray(value) && value.length > 0
          ? [...(value as MultiItem[])]
          : [{ name: "", url: "" }]) as MultiItem[])
      : (value as string);

  const [inputValue, setInputValue] = useState<string | MultiItem[]>(
    initialValue
  );

  const firstFieldRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(
    null
  );

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  useEffect(() => {
    if (firstFieldRef.current) {
      const el = firstFieldRef.current as
        | HTMLInputElement
        | HTMLTextAreaElement;
      el.focus();
      const len = el.value?.length ?? 0;
      try {
        el.setSelectionRange?.(len, len);
      } catch {}
    }
  }, []);

  const sanitizeMulti = (items: MultiItem[]): MultiItem[] =>
    items
      .map(({ name, url }) => ({ name: name.trim(), url: url.trim() }))
      .filter((i) => i.name !== "" && i.url !== "");

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        if (type === "multiLink") {
          const cleaned = sanitizeMulti(inputValue as MultiItem[]);
          if (cleaned.length === 0) onDismissEmpty?.();
        }
        onClose();
        return;
      }
      if (type === "textarea") {
        if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
          e.preventDefault();
          handleSave();
        }
      } else if (type === "text" || type === "link") {
        if (e.key === "Enter") {
          e.preventDefault();
          handleSave();
        }
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [inputValue, type]);

  const handleMultiChange = (
    index: number,
    field: keyof MultiItem,
    val: string
  ) => {
    setInputValue((prev) => {
      const copy = [...(prev as MultiItem[])];
      copy[index] = { ...copy[index], [field]: val };
      return copy;
    });
  };

  const handleAdd = () => {
    const list = inputValue as MultiItem[];
    const last = list.at(-1);
    const isLastFilled =
      !!last && last.name.trim() !== "" && last.url.trim() !== "";
    if (isLastFilled) {
      setInputValue((prev) => [
        ...(prev as MultiItem[]),
        { name: "", url: "" },
      ]);
    }
  };

  const isLastIncomplete = () => {
    if (type !== "multiLink") return false;
    const last = (inputValue as MultiItem[]).at(-1);
    return !last || last.name.trim() === "" || last.url.trim() === "";
  };

  const handleSave = () => {
    if (type === "multiLink") {
      const cleaned = sanitizeMulti(inputValue as MultiItem[]);
      onSave(cleaned);
    } else {
      onSave(typeof inputValue === "string" ? inputValue : "");
    }
  };

  return (
    <ModalOverlay
      onClick={() => {
        if (type === "multiLink") {
          const cleaned = sanitizeMulti(inputValue as MultiItem[]);
          if (cleaned.length === 0) onDismissEmpty?.();
        }
        onClose();
      }}
    >
      <ModalContentWrapper onClick={(e) => e.stopPropagation()}>
        <ModalTitle>{title}</ModalTitle>

        <ScrollableContent>
          {type === "text" && (
            <StyledInput
              ref={firstFieldRef as React.RefObject<HTMLInputElement>}
              type="text"
              value={inputValue as string}
              onChange={(e) => setInputValue(e.target.value)}
            />
          )}

          {type === "textarea" && (
            <StyledTextarea
              ref={firstFieldRef as React.RefObject<HTMLTextAreaElement>}
              value={inputValue as string}
              onChange={(e) => setInputValue(e.target.value)}
            />
          )}

          {type === "link" && (
            <StyledInput
              ref={firstFieldRef as React.RefObject<HTMLInputElement>}
              type="url"
              value={inputValue as string}
              onChange={(e) => setInputValue(e.target.value)}
            />
          )}

          {type === "multiLink" &&
            (inputValue as MultiItem[]).map((item, i) => (
              <div key={i}>
                <ModalField>Nome:</ModalField>
                <StyledInput
                  ref={
                    i === 0
                      ? (firstFieldRef as React.RefObject<HTMLInputElement>)
                      : undefined
                  }
                  type="text"
                  value={item.name}
                  onChange={(e) => handleMultiChange(i, "name", e.target.value)}
                />
                <ModalField>Link:</ModalField>
                <StyledInput
                  type="url"
                  value={item.url}
                  onChange={(e) => handleMultiChange(i, "url", e.target.value)}
                />
              </div>
            ))}

          {type === "multiLink" && (
            <AddButton onClick={handleAdd} disabled={isLastIncomplete()}>
              + Adicionar novo
            </AddButton>
          )}
        </ScrollableContent>

        <FixedFooter>
          <ButtonGradient onClick={handleSave}>Salvar</ButtonGradient>
        </FixedFooter>
      </ModalContentWrapper>
    </ModalOverlay>
  );
};

export default EditSectionModal;
